import * as React from 'react';
import { View } from 'react-native';
import getSymbolFromCurrency from 'currency-symbol-map';
import { runOnJS, useAnimatedProps, useDerivedValue, } from 'react-native-reanimated';
import BlurAmountView from 'design-system/BlurAmountView';
import Text, { styles as textStyles } from 'design-system/Text';
import LogoBankAccount from 'design-system/LogoBankAccount';
import { CurrencyAmountText, rem, signPlusSymbolWidth, Spacer, symbolWidth, } from 'design-system';
import { AnimatedTextInput } from 'design-system/AnimatedComponents';
import useStyles from 'hooks/useStyles';
import { useAppSelector } from 'store/hooks';
import { getBalance } from 'utils/api/getBalance';
import createStyleSheets from 'utils/createStyleSheets';
import { selectIsScrambled } from 'hooks/useScrambleMode';
import { selectDefaultCurrency } from 'reducers/selectors';
import { formatAmount, formatAmountWorklet, formatCryptoAmount, } from 'utils/formatting';
import { isWeb, isAndroid } from '../../../../constants';
const InvestmentInfoView = ({ data, lastItem, selectedInfoObj, }) => {
    const didMount = React.useRef(false);
    const styles = useStyles(styleSet);
    const [internalLastItem, setInternalLastItem] = React.useState(lastItem);
    React.useEffect(() => {
        if (didMount.current) {
            if (lastItem !== undefined) {
                setInternalLastItem(lastItem);
            }
        }
        else {
            didMount.current = true;
        }
    }, [lastItem]);
    const [firstBalance, convertedBalance] = getBalance(data);
    const cryptoBalance = data.type === 'CRYPTO' && firstBalance && convertedBalance
        ? firstBalance
        : null;
    const primaryBalance = cryptoBalance ? convertedBalance : firstBalance;
    const isScrambled = useAppSelector(selectIsScrambled);
    const defaultCurrency = useAppSelector(selectDefaultCurrency);
    const currencyStr = React.useMemo(() => getSymbolFromCurrency(primaryBalance?.currency || defaultCurrency) || '', [defaultCurrency, primaryBalance?.currency]);
    const fallbackInfo = React.useMemo(() => {
        const fallbackAmount = primaryBalance?.amount || 0;
        const amount = formatAmount(Math.abs(fallbackAmount), undefined, 2);
        const sign = fallbackAmount < 0 ? '-' : '';
        const [decimal, fraction] = amount.split('.');
        return {
            decimal,
            fraction: `.${fraction}`,
            currencyAndSign: sign + currencyStr,
            currencyAndSignWidth: sign ? signPlusSymbolWidth : symbolWidth,
        };
    }, [currencyStr, primaryBalance?.amount]);
    const infoSharedValue = useDerivedValue(() => {
        if (selectedInfoObj.value) {
            const valueToUse = selectedInfoObj.value.actualY;
            const amount = formatAmountWorklet(Math.abs(valueToUse), '');
            const sign = valueToUse < 0 ? '-' : '';
            const [decimal, fraction] = amount.split('.');
            return {
                decimal,
                fraction: `.${fraction}`,
                currencyAndSign: sign + selectedInfoObj.value.currencySymbol,
                currencyAndSignWidth: sign ? signPlusSymbolWidth : symbolWidth,
            };
        }
        return fallbackInfo;
    }, [fallbackInfo, selectedInfoObj]);
    const decimalProps = useAnimatedProps(() => ({
        text: infoSharedValue.value.decimal,
    }), [infoSharedValue]);
    const currencyProps = useAnimatedProps(() => ({
        text: infoSharedValue.value.currencyAndSign,
        ...(isAndroid
            ? {
                width: infoSharedValue.value.currencyAndSignWidth,
            }
            : {}),
    }), [infoSharedValue]);
    const fractionProps = useAnimatedProps(() => ({
        text: infoSharedValue.value.fraction,
    }), [infoSharedValue]);
    const timeProps = useAnimatedProps(() => ({
        text: selectedInfoObj.value
            ? String(selectedInfoObj.value.label)
            : internalLastItem?.label || '',
    }), [internalLastItem?.label, selectedInfoObj]);
    const [webState, setWebState] = React.useState(undefined);
    useDerivedValue(() => {
        if (isWeb) {
            runOnJS(setWebState)(selectedInfoObj.value);
        }
    }, [selectedInfoObj]);
    let fallbackSecondaryLabel = null;
    if (cryptoBalance) {
        fallbackSecondaryLabel = formatCryptoAmount(cryptoBalance.amount, cryptoBalance.currency);
    }
    else if (convertedBalance) {
        fallbackSecondaryLabel = formatAmount(convertedBalance.amount, convertedBalance.currency, 2);
    }
    const secondaryProps = useAnimatedProps(() => ({
        text: selectedInfoObj.value
            ? selectedInfoObj.value.secondaryLabel ?? ''
            : fallbackSecondaryLabel ?? '',
    }), [fallbackSecondaryLabel, selectedInfoObj]);
    const AmountContent = React.useMemo(() => {
        if (isWeb) {
            return (<>
          <CurrencyAmountText value={{
                    amount: typeof webState?.actualY === 'number'
                        ? webState?.actualY
                        : primaryBalance?.amount || 0,
                    currency: currencyStr,
                }} size={'L'}/>
          {fallbackSecondaryLabel !== null ? (<BlurAmountView enabled={isScrambled}>
              <Text NumbersThin-16 Secondary numberOfLines={1}>
                {webState
                        ? webState.secondaryLabel ?? ''
                        : fallbackSecondaryLabel}
              </Text>
            </BlurAmountView>) : null}
          <Text TextThin-14 Secondary>
            {webState?.label || internalLastItem?.label || ' '}
          </Text>
        </>);
        }
        return (<>
        <View style={styles.row}>
          <BlurAmountView enabled={isScrambled}>
            <View style={styles.amountView}>
              <AnimatedTextInput editable={false} numberOfLines={1} allowFontScaling={false} animatedProps={currencyProps} defaultValue={fallbackInfo.currencyAndSign} style={[
                styles.currencyStyles,
                isAndroid && { width: fallbackInfo.currencyAndSignWidth },
            ]}/>
              <Spacer w={2}/>
              <AnimatedTextInput editable={false} numberOfLines={1} allowFontScaling={false} animatedProps={decimalProps} style={styles.absAmountStyles} defaultValue={fallbackInfo.decimal}/>
              <AnimatedTextInput editable={false} numberOfLines={1} allowFontScaling={false} animatedProps={fractionProps} style={styles.fractionStyles} defaultValue={fallbackInfo.fraction}/>
            </View>
          </BlurAmountView>
        </View>
        {fallbackSecondaryLabel !== null ? (<BlurAmountView enabled={isScrambled}>
            <AnimatedTextInput editable={false} numberOfLines={1} allowFontScaling={false} style={styles.secondaryStyles} animatedProps={secondaryProps} defaultValue={fallbackSecondaryLabel}/>
          </BlurAmountView>) : null}
        <AnimatedTextInput editable={false} numberOfLines={1} allowFontScaling={false} style={styles.timeStyles} animatedProps={timeProps} defaultValue={internalLastItem?.label || ''}/>
      </>);
    }, [
        secondaryProps,
        currencyProps,
        currencyStr,
        decimalProps,
        fallbackInfo.currencyAndSign,
        fallbackInfo.currencyAndSignWidth,
        fallbackInfo.decimal,
        fallbackInfo.fraction,
        fractionProps,
        internalLastItem?.label,
        isScrambled,
        primaryBalance?.amount,
        styles.absAmountStyles,
        styles.amountView,
        styles.currencyStyles,
        styles.fractionStyles,
        styles.row,
        styles.timeStyles,
        timeProps,
        fallbackSecondaryLabel,
        styles.secondaryStyles,
        webState,
    ]);
    return (<View style={styles.container}>
      <LogoBankAccount size="R44" onCard data={data} showHidden/>
      {AmountContent}
    </View>);
};
export default React.memo(InvestmentInfoView);
const styleSet = createStyleSheets((colors) => ({
    container: {
        paddingTop: rem(16),
        paddingHorizontal: rem(16),
    },
    row: {
        flexDirection: 'row',
    },
    currencyStyles: {
        margin: 0,
        padding: 0,
        fontSize: rem(30),
        lineHeight: rem(41),
        color: colors.text.primary,
        fontFamily: 'Nunito-ExtraBold',
    },
    absAmountStyles: {
        margin: 0,
        padding: 0,
        fontSize: rem(36),
        lineHeight: rem(48),
        color: colors.text.primary,
        fontFamily: 'Nunito-ExtraBold',
    },
    fractionStyles: {
        margin: 0,
        padding: 0,
        fontSize: rem(24),
        lineHeight: rem(38),
        color: colors.text.primary,
        fontFamily: 'Nunito-ExtraBold',
    },
    amountView: {
        paddingTop: rem(6),
        flexDirection: 'row',
        // Android stretches each input to the row's height and centres its text,
        // so different font sizes end up on different baselines. Yoga can align
        // the inputs' baselines directly instead.
        ...(isAndroid && { alignItems: 'baseline' }),
    },
    secondaryStyles: {
        margin: 0,
        padding: 0,
        marginBottom: rem(4),
        color: colors.text.secondary,
        fontSize: textStyles.Numbers_16.fontSize,
        fontFamily: textStyles.Numbers_16.fontFamily,
    },
    timeStyles: {
        margin: 0,
        padding: 0,
        color: colors.text.secondary,
        fontSize: textStyles.TextThin_14.fontSize,
        fontFamily: textStyles.TextThin_14.fontFamily,
    },
}));
